import { ButtonSmallSecondary } from "@studio3t/generic-components";
import { useCallback, useId, useRef, useState } from "react";
import styled from "styled-components";
import {
  FieldRow,
  FieldRowControl,
  FormSection,
  HelpText,
  HiddenFileInput,
  Input,
  Label,
  PanelTextarea,
  SectionTitle,
  Subsection,
  SubsectionText,
  SubsectionTitle,
  UploadFileName,
  UploadPanel,
  UploadPanelDivider,
  UploadPanelRow,
  UploadRow,
} from "./styles";

const JsonError = styled.span`
  font-size: 11px;
  color: #dc2626;
`;

// The service-account key subsection: upload a .json file or paste the
// content, with the live JSON verdict under the textarea.
function ServiceAccountKeyField({
  accountKeyContent,
  setAccountKeyContent,
}: {
  accountKeyContent: string;
  setAccountKeyContent: (value: string) => void;
}) {
  const accountKeyContentId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Presentational only: which file filled the textarea. Cleared on manual
  // edits, since the content no longer matches the chosen file.
  const [fileName, setFileName] = useState<string | null>(null);
  const hasJsonError =
    accountKeyContent.trim() !== "" && !isValidJson(accountKeyContent);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          setAccountKeyContent(reader.result);
        }
      };
      reader.readAsText(file);
      e.currentTarget.value = "";
    },
    [setAccountKeyContent],
  );

  return (
    <Subsection>
      <SubsectionTitle>Service Account Key JSON*</SubsectionTitle>
      <SubsectionText>
        Upload a .json key file or paste the service account JSON content.
      </SubsectionText>

      <UploadPanel>
        <UploadPanelRow>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".json"
            aria-label="Upload service account key JSON file"
            onChange={handleFileUpload}
          />
          <UploadRow>
            <ButtonSmallSecondary
              // GenericButton renders a <button> without a type attribute,
              // which defaults to "submit" inside the form — clicking it
              // would fire native validation ("Please fill out this
              // field") and, on a valid form, submit it.
              ref={(el) => el?.setAttribute("type", "button")}
              text="Upload a .json key from file"
              onClick={() => fileInputRef.current?.click()}
            />
            <UploadFileName>{fileName ?? "No file chosen"}</UploadFileName>
          </UploadRow>
        </UploadPanelRow>

        <UploadPanelDivider />

        <UploadPanelRow>
          <PanelTextarea
            id={accountKeyContentId}
            value={accountKeyContent}
            onChange={(e) => {
              setAccountKeyContent(e.target.value);
              setFileName(null);
            }}
            placeholder="Or paste the service account JSON"
            required
            aria-invalid={hasJsonError}
          />
          <HelpText>
            {
              'Example: {"type":"service_account","project_id":"...","private_key":"...","client_email":"..."}'
            }
          </HelpText>
          {hasJsonError && (
            <JsonError>
              Not valid JSON — check the uploaded file or pasted content
            </JsonError>
          )}
        </UploadPanelRow>
      </UploadPanel>
    </Subsection>
  );
}

// Validates uploaded/pasted key content live; the submit guard in
// usePubSubFields uses the same check.
export function isValidJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

interface PubSubFieldsProps {
  projectId: string;
  setProjectId: (value: string) => void;
  accountKeyContent: string;
  setAccountKeyContent: (value: string) => void;
  subscription: string;
  setSubscription: (value: string) => void;
}

export function PubSubFields({
  projectId,
  setProjectId,
  accountKeyContent,
  setAccountKeyContent,
  subscription,
  setSubscription,
}: PubSubFieldsProps) {
  const projectIdId = useId();
  const subscriptionId = useId();

  return (
    <>
      <FormSection>
        <SectionTitle>Google Pub/Sub configuration</SectionTitle>

        <FieldRow>
          <Label htmlFor={projectIdId}>GCP Project ID*</Label>
          <FieldRowControl>
            <Input
              id={projectIdId}
              type="text"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="my-gcp-project"
              required
            />
            <HelpText>Google Cloud project ID for Pub/Sub</HelpText>
          </FieldRowControl>
        </FieldRow>

        <ServiceAccountKeyField
          accountKeyContent={accountKeyContent}
          setAccountKeyContent={setAccountKeyContent}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>Schema introspection (optional)</SectionTitle>

        <FieldRow>
          <Label htmlFor={subscriptionId}>Subscription</Label>
          <FieldRowControl>
            <Input
              id={subscriptionId}
              type="text"
              value={subscription}
              onChange={(e) => setSubscription(e.target.value)}
              placeholder="my-introspection-sub"
            />
            <HelpText>
              Subscription used for schema introspection. When empty, falls back
              to the resource name.
            </HelpText>
          </FieldRowControl>
        </FieldRow>
      </FormSection>
    </>
  );
}
